import { put, call, takeLatest, type CallEffect, type PutEffect, type ForkEffect } from 'redux-saga/effects';
import { type AnyAction } from 'redux';
import { AImagineActionType } from './enums';
import { type AImagineAction } from './interfaces';
import { generateImage, uploadDocument, activateDocument } from './api';
import { uploadFile } from '@utilities';

type AImagineSagaEffect = Generator<CallEffect<any> | PutEffect<AImagineAction>>;
type AImagineSagaFormEffect = Generator<ForkEffect<void>>;

// ---------------------------------------------------------------------------
// Upload input image
// ---------------------------------------------------------------------------

/**
 * Worker saga for UPLOAD_INPUT_IMAGE.
 *
 * Flow: POST /document/upload → S3 upload → POST /document/activate
 * Returns documentId + viewable URL via SET_UPLOADED_IMAGE so the caller
 * can pass documentId into GenerateImageRequest.
 *
 * Payload shape:
 * {
 *   file: File;
 *   successCB?: (documentId: number) => void;
 *   errorCB?:   (message: string) => void;
 *   finalCB?:   () => void;
 * }
 */
export function* uploadInputImageEffect(action: AnyAction): AImagineSagaEffect {
	const { file, successCB, errorCB, finalCB } = action.payload;

	const uploadRequest = {
		reference_id: file.name,
		reference_type: 'USER',
		documents: [
			{
				category: 'INSPIRATION_IMAGES',
				sub_category: 'INSPIRATION_AIGENERATED',
				attachments: [{ file: file.name, cover_image: true }],
				document_id: 0
			}
		]
	};

	try {
		const { data }: any = yield call(uploadDocument, uploadRequest);

		const responses = data?.documents_upload_responses ?? data?.documentsUploadResponses;
		if (!responses?.length) {
			errorCB?.('Upload did not return document');
			return;
		}

		const docResponse = responses[0];
		const documentId = docResponse?.document_id ?? docResponse?.documentId;
		const attachments = docResponse?.attachments;
		if (documentId == null || !attachments?.length) {
			errorCB?.('Invalid upload response');
			return;
		}

		const firstAttachment = attachments[0];
		const fileName = firstAttachment?.file_name ?? firstAttachment?.fileName ?? file.name;

		// This is the S3 pre-signed PUT URL — only used for uploading, NOT for display.
		const uploadUrl = firstAttachment?.document_url ?? firstAttachment?.documentUrl;
		if (!uploadUrl) {
			errorCB?.('Upload response missing document_url');
			return;
		}

		// Step 1: PUT the file to S3
		yield call(uploadFile, uploadUrl, file, fileName);

		// Step 2: Activate the document — capture the response to get the viewable URL
		const activateResponse: any = yield call(activateDocument, { documentIds: [documentId] });

		// Extract the viewable URL from the activate response.
		// The activate endpoint should return the permanent/CDN URL for the document.
		// Adjust the path below to match your actual activate API response shape.
		const activatedDocs =
			activateResponse?.data?.documents ?? activateResponse?.data?.data ?? activateResponse?.data ?? [];

		const activatedDoc = Array.isArray(activatedDocs)
			? (activatedDocs.find((d: any) => (d?.document_id ?? d?.documentId) === documentId) ?? activatedDocs[0])
			: activatedDocs;

		// Try common field names for the viewable URL in activate responses
		const viewableUrl =
			activatedDoc?.url ??
			activatedDoc?.document_url ??
			activatedDoc?.documentUrl ??
			activatedDoc?.image_url ??
			activatedDoc?.imageUrl ??
			activatedDoc?.file_url ??
			activatedDoc?.fileUrl ??
			null;

		// Dispatch with the real viewable URL so the canvas can swap away from base64.
		// If the activate response doesn't carry a URL (some backends don't), we fall
		// back to null so the component keeps showing the base64 preview — still safe.
		yield put({
			type: AImagineActionType.SET_UPLOADED_IMAGE,
			payload: {
				documentId,
				imageUrl: viewableUrl // null-safe: component falls back to base64 preview
			}
		});

		successCB?.(documentId);
	} catch (error: any) {
		errorCB?.(error?.message || 'Failed to upload image');
	} finally {
		if (finalCB) finalCB();
	}
}

// ---------------------------------------------------------------------------
// Generate image
// ---------------------------------------------------------------------------

/**
 * Worker saga for GENERATE_IMAGE.
 *
 * Payload shape:
 * {
 *   data: GenerateImageRequest;
 *   successCB?: (response: GenerateImageResponse) => void;
 *   errorCB?:   (message: string) => void;
 *   finalCB?:   () => void;
 * }
 */
export function* generateImageEffect(action: AnyAction): AImagineSagaEffect {
	const { data, successCB, errorCB, finalCB } = action.payload;

	yield put({
		type: AImagineActionType.SET_GENERATE_LOADING,
		payload: true
	});

	try {
		const { data: responseData }: any = yield call(generateImage, data);

		if (!responseData.success) {
			const errorMessage = responseData.errorMessage || 'Image generation failed';

			yield put({
				type: AImagineActionType.SET_GENERATE_ERROR,
				payload: errorMessage
			});

			if (errorCB) errorCB(errorMessage);
			return;
		}

		yield put({
			type: AImagineActionType.STORE_GENERATED_IMAGE,
			payload: responseData
		});

		if (successCB) successCB(responseData);
	} catch (error: any) {
		const errorMessage = error?.message || 'Failed to generate image';

		yield put({
			type: AImagineActionType.SET_GENERATE_ERROR,
			payload: errorMessage
		});

		if (errorCB) errorCB(errorMessage);
	} finally {
		yield put({
			type: AImagineActionType.SET_GENERATE_LOADING,
			payload: false
		});

		if (finalCB) finalCB();
	}
}

// ---------------------------------------------------------------------------
// Watcher sagas
// ---------------------------------------------------------------------------

export function* uploadInputImageSaga(): AImagineSagaFormEffect {
	yield takeLatest(AImagineActionType.UPLOAD_INPUT_IMAGE, uploadInputImageEffect);
}

export function* generateImageSaga(): AImagineSagaFormEffect {
	yield takeLatest(AImagineActionType.GENERATE_IMAGE, generateImageEffect);
}
